import type { JSX } from "react";

import type { AttributionMode } from "../types/visibility";

interface AttributionMeta {
  label: string;
  explanation: string;
  className: string;
}

const META: Record<AttributionMode, AttributionMeta> = {
  retrieved: {
    label: "From cited sources",
    explanation: "The engine cited web sources, and they back this business.",
    className: "bg-emerald-500/20 text-emerald-600 border-emerald-500/30",
  },
  parametric: {
    label: "From model memory",
    explanation: "The engine cited no sources — this name came from its training data.",
    className: "bg-purple-500/20 text-purple-600 border-purple-500/30",
  },
  unattributed: {
    label: "Source unclear",
    explanation: "Sources exist for this answer, but can't yet be tied to this business.",
    className: "bg-surface-container-high text-on-surface-variant border-surface-container-high",
  },
};

interface VisibilityAttributionBadgeProps {
  attribution: AttributionMode;
  compact?: boolean;
}

/**
 * The three-mode "how did this business come to be named" badge, with a hover tooltip explaining
 * the mode. Reused on hotel cards and inside the Why panel's business list.
 */
const VisibilityAttributionBadge = ({
  attribution,
  compact = false,
}: VisibilityAttributionBadgeProps): JSX.Element | null => {
  const meta = META[attribution];
  if (!meta) return null;

  return (
    <span className="relative group inline-flex">
      <span
        className={`inline-flex items-center gap-1 rounded-full border font-semibold ${meta.className} ${
          compact ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1"
        }`}
      >
        {meta.label}
      </span>
      <span
        role="tooltip"
        className="absolute bottom-full left-0 mb-2 z-50 w-64 rounded-lg bg-gray-900 p-3 text-xs text-white shadow-xl opacity-0 pointer-events-none transition-opacity duration-150 group-hover:opacity-100"
      >
        {meta.explanation}
      </span>
    </span>
  );
};

export default VisibilityAttributionBadge;
